import React from 'react';
import { Button, Dialog, Flex, Text } from '@/components/ui';
import { IconX } from '@tabler/icons-react';

// Types
import { ColumnMapping, FormField } from './types';

interface FormFieldSelectionModalProps {
  fieldDataPoint?: string; // The field we're mapping to (if opening from field click)
  columnName?: string; // The column we're mapping from (if opening from column click)
  formFields: FormField[];
  availableColumns: Array<{
    name: string;
    isAlreadyMapped: boolean;
    currentMapping?: ColumnMapping;
  }>;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onFieldSelect: (fieldDataPoint: string) => void;
  onRemoveMapping?: () => void;
  currentMappedField?: string;
}

export const FormFieldSelectionModal: React.FC<
  FormFieldSelectionModalProps
> = ({
  fieldDataPoint,
  columnName,
  formFields,
  availableColumns,
  open,
  onOpenChange,
  onFieldSelect,
  onRemoveMapping,
  currentMappedField,
}) => {
  // Determine modal mode: if fieldDataPoint is provided, we're selecting a column for a field
  // if columnName is provided, we're selecting a field for a column
  const isSelectingColumn = !!fieldDataPoint;

  // State for selected item (field or column depending on mode)
  const [selectedItem, setSelectedItem] = React.useState<string>('');

  // Find current mappings
  const fieldMappedToCurrentColumn = columnName
    ? availableColumns.find(
        col =>
          col.name === columnName &&
          col.currentMapping?.mappingType === 'formField'
      )?.currentMapping?.targetField
    : undefined;

  const columnMappedToCurrentField = fieldDataPoint
    ? availableColumns.find(
        col =>
          col.currentMapping?.mappingType === 'formField' &&
          col.currentMapping?.targetField === fieldDataPoint
      )?.name
    : undefined;

  // Initialize selected item when modal opens
  React.useEffect(() => {
    if (open) {
      if (isSelectingColumn) {
        // Selecting column for field - preselect current mapped column
        setSelectedItem(columnMappedToCurrentField || '');
      } else {
        // Selecting field for column - preselect current mapped field
        setSelectedItem(fieldMappedToCurrentColumn || '');
      }
    }
  }, [
    open,
    isSelectingColumn,
    columnMappedToCurrentField,
    fieldMappedToCurrentColumn,
  ]);

  const handleConfirm = () => {
    if (selectedItem) {
      onFieldSelect(selectedItem);
      onOpenChange(false);
    }
  };

  const handleRemove = () => {
    if (onRemoveMapping) {
      onRemoveMapping();
      onOpenChange(false);
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Content size="medium" style={{ maxWidth: '500px' }}>
        <Dialog.Header
          title={
            isSelectingColumn
              ? `Map Form Field "${fieldDataPoint}" to Column`
              : `Map Column "${columnName}" to Form Field`
          }
          subtitle={
            isSelectingColumn
              ? `Choose which column from your file to map to the form field "${fieldDataPoint}".${currentMappedField ? ` Currently mapped to column "${currentMappedField}".` : ''}`
              : `Choose which form field to map the column "${columnName}" to.${currentMappedField ? ` Currently mapped to field "${currentMappedField}".` : ''}`
          }
          endSlot={
            <Button
              variant="surface"
              size="medium"
              onClick={() => onOpenChange(false)}
              title="Close"
            >
              <IconX />
            </Button>
          }
        />

        <Dialog.Body>
          <Flex direction="column" gap="4">
          <Text size="2" weight="medium">
            {isSelectingColumn
              ? 'Available Columns:'
              : 'Available Form Fields:'}
          </Text>

          <Flex
            direction="column"
            gap="2"
            style={{ maxHeight: '300px', overflowY: 'auto' }}
          >
            {isSelectingColumn
              ? // Show columns when selecting column for field
                availableColumns.map(column => {
                  const isAlreadyMapped =
                    column.currentMapping &&
                    column.currentMapping.mappingType !== 'ignored';
                  const isCurrentMapping =
                    column.name === columnMappedToCurrentField;

                  return (
                    <Flex key={column.name} align="center" gap="2">
                      <input
                        type="radio"
                        name="itemSelection"
                        value={column.name}
                        checked={selectedItem === column.name}
                        onChange={e => setSelectedItem(e.target.value)}
                        style={{ margin: 0 }}
                      />
                      <Flex direction="column" gap="1" style={{ flex: 1 }}>
                        <Text size="2">{column.name}</Text>
                        {isCurrentMapping && (
                          <Text size="1" color="green">
                            ✓ Currently mapped to this field
                          </Text>
                        )}
                        {isAlreadyMapped && !isCurrentMapping && (
                          <Text size="1" color="orange">
                            ⚠️ Already mapped to{' '}
                            {column.currentMapping?.mappingType === 'metadata'
                              ? `metadata "${column.currentMapping.metadataType?.label || 'unknown'}"`
                              : `field "${column.currentMapping?.targetField || 'unknown'}"`}
                          </Text>
                        )}
                        {isAlreadyMapped && !isCurrentMapping && (
                          <Text size="1" color="gray">
                            Selecting this will remove the existing mapping
                          </Text>
                        )}
                      </Flex>
                    </Flex>
                  );
                })
              : // Show fields when selecting field for column
                formFields.map(field => {
                  const isAlreadyMapped = availableColumns.some(
                    col =>
                      col.currentMapping?.mappingType === 'formField' &&
                      col.currentMapping?.targetField === field.dataPoint &&
                      col.name !== columnName
                  );
                  const isCurrentMapping =
                    field.dataPoint === fieldMappedToCurrentColumn;

                  return (
                    <Flex key={field.dataPoint} align="center" gap="2">
                      <input
                        type="radio"
                        name="itemSelection"
                        value={field.dataPoint}
                        checked={selectedItem === field.dataPoint}
                        onChange={e => setSelectedItem(e.target.value)}
                        style={{ margin: 0 }}
                      />
                      <Flex direction="column" gap="1" style={{ flex: 1 }}>
                        <Text size="2">{field.dataPoint}</Text>
                        {isCurrentMapping && (
                          <Text size="1" color="green">
                            ✓ Currently mapped to this column
                          </Text>
                        )}
                        {isAlreadyMapped && (
                          <Text size="1" color="orange">
                            ⚠️ Already mapped to another column
                          </Text>
                        )}
                        {isAlreadyMapped && (
                          <Text size="1" color="gray">
                            Selecting this will remove the existing mapping
                          </Text>
                        )}
                      </Flex>
                    </Flex>
                  );
                })}
          </Flex>

          {(isSelectingColumn ? availableColumns.length : formFields.length) ===
            0 && (
            <Text
              size="2"
              color="gray"
              style={{ textAlign: 'center', padding: '20px' }}
            >
              {isSelectingColumn
                ? 'No columns available.'
                : 'No form fields available.'}
            </Text>
          )}
        </Flex>

        <Flex justify="between" mt="6" gap="2">
          {((isSelectingColumn && columnMappedToCurrentField) ||
            (!isSelectingColumn && fieldMappedToCurrentColumn)) &&
            onRemoveMapping && (
              <Button variant="outlined" color="error" onClick={handleRemove}>
                Remove Mapping
              </Button>
            )}
          <Flex gap="2" ml="auto">
            <Button variant="outlined" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleConfirm}
              disabled={!selectedItem}
            >
              {(isSelectingColumn && columnMappedToCurrentField) ||
              (!isSelectingColumn && fieldMappedToCurrentColumn)
                ? 'Update Mapping'
                : 'Create Mapping'}
            </Button>
          </Flex>
          </Flex>
        </Dialog.Body>
      </Dialog.Content>
    </Dialog.Root>
  );
};

export default FormFieldSelectionModal;
